import { useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { ResultActions } from "./ResultActions";
import { HistoryStrip } from "./HistoryStrip";
import { webVariant, fallbackTo } from "../lib/imageVariants";
import { EditWorkspaceModal } from "./EditWorkspaceModal";

export function Canvas() {
  const currentImage = useAppStore((s) => s.currentImage);
  const activeGenerations = useAppStore((s) => s.activeGenerations);
  const partialImage = useAppStore((s) => s.classicPartialImage);
  const classicPhase = useAppStore((s) => s.classicPhase);
  const quality = useAppStore((s) => s.quality);
  const getResolvedSize = useAppStore((s) => s.getResolvedSize);
  const showToast = useAppStore((s) => s.showToast);
  const setPrompt = useAppStore((s) => s.setPrompt);
  const openLightbox = useAppStore((s) => s.openLightbox);

  const [promptView, setPromptView] = useState<"enhanced" | "original">("enhanced");
  const [editOpen, setEditOpen] = useState(false);

  const hasOriginal = Boolean(
    currentImage?.originalPrompt &&
      currentImage.originalPrompt !== currentImage.prompt,
  );
  const showingOriginal = hasOriginal && promptView === "original";
  const visiblePrompt = showingOriginal
    ? currentImage?.originalPrompt
    : currentImage?.prompt;
  const visibleSystemPrompt = currentImage?.systemPrompt?.trim() || "";

  const copyVisible = () => {
    if (!visiblePrompt) return;
    void navigator.clipboard.writeText(visiblePrompt);
    showToast(
      showingOriginal
        ? "원본 프롬프트를 복사했습니다"
        : "수정됨 프롬프트를 복사했습니다",
    );
  };
  const copySystemPrompt = () => {
    if (!visibleSystemPrompt) return;
    void navigator.clipboard.writeText(visibleSystemPrompt);
    showToast("기본 프롬프트를 복사했습니다");
  };

  // currentImage.quality 는 우리가 "요청한" 화질이다. 백엔드가 그대로 쓰지
  // 않으므로(요청 high 가 low~medium 으로 적용된다) 실제 적용값이 기록돼
  // 있으면 그쪽을 보여준다. 기록이 없는 2026-09-09 이전 생성분만 요청값으로
  // 물러난다.
  const displayQuality =
    currentImage?.promptRuntime?.effectiveImage?.quality ??
    currentImage?.quality ??
    quality;
  // 크기도 같다. currentImage.size 는 요청값이라 "auto" 나 고르지도 않은 픽셀이
  // 찍힌다. 서버가 잰 실제 해상도 → 백엔드가 보고한 실제 크기 → (기록이 없는
  // 옛 생성분에 한해) 요청값 순으로 내려간다.
  const displaySize =
    currentImage?.resolution ||
    (currentImage?.width && currentImage?.height
      ? `${currentImage.width}x${currentImage.height}`
      : currentImage?.promptRuntime?.effectiveImage?.size ??
        currentImage?.size ??
        getResolvedSize());

  const isGenerating = activeGenerations > 0;
  const showSkeleton = !currentImage && isGenerating;
  const showEmpty = !currentImage && !isGenerating;

  const tryExamplePrompt = (text: string) => {
    setPrompt(text);
    const el = document.querySelector<HTMLTextAreaElement>(
      ".composer__textarea",
    );
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  };

  return (
    <main className="canvas">
      <div className={`progress-bar${isGenerating ? " active" : ""}`} />

      {showEmpty ? (
        <div className="canvas-empty">
          <div className="canvas-empty__icon" aria-hidden="true">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
          <div className="canvas-empty__title">무엇을 만들어볼까요?</div>
          <div className="canvas-empty__hint">
            왼쪽에 프롬프트를 입력하고{" "}
            <kbd>Ctrl</kbd>+<kbd>Enter</kbd> 로 생성하세요.
          </div>
          <div className="canvas-empty__examples">
            {[
              "비 내리는 도쿄 골목, 네온 반사, 시네마틱 사진",
              "미니멀한 한국 전통 수묵화, 소나무와 학",
              "우주복을 입은 시바견, 달 표면, 사실적 렌더링",
            ].map((p) => (
              <button
                key={p}
                type="button"
                className="canvas-empty__example"
                onClick={() => tryExamplePrompt(p)}
                title="이 예시를 프롬프트로 사용"
              >
                {p}
              </button>
            ))}
          </div>
          <div className="canvas-empty__shortcuts">
            팁: 이미지를 드래그&amp;드롭하거나 붙여넣기(<kbd>Ctrl</kbd>+<kbd>V</kbd>)로 참조 추가 ·{" "}
            <kbd>?</kbd> 로 단축키 전체 보기
          </div>
        </div>
      ) : showSkeleton && partialImage ? (
        <div className="canvas-partial" role="status" aria-live="polite">
          <img src={partialImage} alt="생성 중 미리보기" />
          <div className="canvas-partial__badge">미리보기 · 마무리 중</div>
        </div>
      ) : showSkeleton ? (
        <div className="canvas-skeleton" role="status" aria-live="polite">
          <div className="canvas-skeleton__frame">
            <div className="canvas-skeleton__shimmer" aria-hidden="true" />
          </div>
          <div className="canvas-skeleton__label">
            {activeGenerations > 1
              ? `${activeGenerations}개 이미지를 생성하고 있어요…`
              : "이미지를 생성하고 있어요…"}
          </div>
          <div className="canvas-skeleton__sub">
            품질에 따라 10–60초 정도 걸릴 수 있습니다.
          </div>
        </div>
      ) : currentImage ? (
        <div className="result-container visible">
          {isGenerating ? (
            <div className="result-pending-badge" aria-live="polite">
              추가 생성 중 {activeGenerations}개…
            </div>
          ) : null}
          {isGenerating && partialImage ? (
            <div className="result-partial-overlay" role="status" aria-live="polite">
              <img src={partialImage} alt="추가 생성 중 미리보기" />
              <span>{classicPhase === "partial" ? "미리보기 · 마무리 중" : "생성 준비 중"}</span>
            </div>
          ) : null}
          <img
            className="result-img"
            key={currentImage.filename ?? currentImage.url ?? currentImage.image}
            src={(currentImage as any).web ?? webVariant(currentImage.url ?? null) ?? currentImage.url ?? currentImage.image}
            onError={fallbackTo(currentImage.url ?? currentImage.image)}
            alt="생성 결과"
            role="button"
            tabIndex={0}
            title="클릭하여 전체 보기"
            onClick={() => openLightbox()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openLightbox();
              }
            }}
            style={{ cursor: "zoom-in" }}
          />
          {displaySize ? <div className="result-size-pill">{displaySize}</div> : null}
          {visiblePrompt ? (
            <div
              className={`result-prompt${showingOriginal ? " result-prompt--original" : ""}`}
              onClick={copyVisible}
              title="클릭하여 프롬프트 복사"
            >
              {hasOriginal ? (
                <div
                  className="result-prompt__toggle"
                  role="tablist"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!showingOriginal}
                    className={`result-prompt__tab${!showingOriginal ? " is-active" : ""}`}
                    onClick={() => setPromptView("enhanced")}
                  >
                    수정됨
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={showingOriginal}
                    className={`result-prompt__tab${showingOriginal ? " is-active" : ""}`}
                    onClick={() => setPromptView("original")}
                  >
                    원본
                  </button>
                </div>
              ) : null}
              <div className="result-prompt__label">
                {showingOriginal ? "원본 프롬프트" : "수정됨 프롬프트"}
              </div>
              <div className="result-prompt__text">{visiblePrompt}</div>
            </div>
          ) : null}
          {visibleSystemPrompt ? (
            <div
              className="result-system-prompt"
              onClick={copySystemPrompt}
              title="클릭하여 기본 프롬프트 복사"
            >
              <div className="result-system-prompt__label">기본 프롬프트</div>
              <div className="result-system-prompt__text">{visibleSystemPrompt}</div>
            </div>
          ) : null}
          <div className="result-meta">
            {[
              currentImage.elapsed != null ? `${currentImage.elapsed}s` : null,
              currentImage.usage
                ? `${currentImage.usage.total_tokens ?? "?"} 토큰`
                : null,
              displayQuality,
              displaySize,
              currentImage.provider ?? null,
            ]
              .filter((v): v is string => Boolean(v))
              .join(" · ")}
          </div>
          <ResultActions onEdit={() => setEditOpen(true)} />
        </div>
      ) : null}
      <HistoryStrip className="history-strip--canvas" />
      <EditWorkspaceModal open={editOpen} onClose={() => setEditOpen(false)} />
    </main>
  );
}
